import React, { useEffect, useState } from "react";
import SearchInput from "./components/SearchInput.jsx";
import LightningOutHost from "./components/LightningOutHost.jsx";
import SplitView from "./SplitView.jsx";
import ServiceView from "./ServiceView.jsx";

// Read ?id / ?sobj / ?name from the current URL. `id` alone is enough to
// resolve the record via Apex; sobj/name just seed a faster first paint.
function readRouteFromUrl() {
    const p = new URLSearchParams(window.location.search);
    const id = p.get("id");
    if (!id) return { mode: "list", selectedRecord: null };
    return {
        mode: "detail",
        selectedRecord: {
            id,
            sobjectType: p.get("sobj") || "",
            name: p.get("name") || "",
        },
    };
}

// Serialize app state into a URL search string (no leading "?").
function buildUrl(mode, selectedRecord) {
    if (mode !== "detail" || !selectedRecord?.id) return "/";
    const p = new URLSearchParams();
    p.set("id", selectedRecord.id);
    if (selectedRecord.sobjectType) p.set("sobj", selectedRecord.sobjectType);
    if (selectedRecord.name) p.set("name", selectedRecord.name);
    return "/?" + p.toString();
}

export default function App() {
    const [authStatus, setAuthStatus] = useState({ loading: true });
    const [keyword, setKeyword] = useState("");
    // Seed mode + selectedRecord from the URL so deep links land on detail.
    const initial = readRouteFromUrl();
    const [mode, setMode] = useState(initial.mode);
    const [selectedRecord, setSelectedRecord] = useState(
        initial.selectedRecord,
    );

    // LWC → React: a row was clicked in c-search-results.
    const handleRecordSelect = (detail) => {
        setSelectedRecord(detail);
        setMode("detail");
        window.history.pushState({}, "", buildUrl("detail", detail));
    };

    // LWC → React: Back button in c-record-detail.
    const handleHostNavigate = (detail) => {
        if (!detail || detail.target === "home") {
            setMode("list");
            setSelectedRecord(null);
            window.history.pushState({}, "", "/");
        }
    };

    // Keep app state in sync with browser back/forward.
    useEffect(() => {
        const onPop = () => {
            const r = readRouteFromUrl();
            setMode(r.mode);
            setSelectedRecord(r.selectedRecord);
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch("/api/auth/status");
                const data = await res.json();
                setAuthStatus({ loading: false, ...data });
            } catch (e) {
                setAuthStatus({
                    loading: false,
                    authenticated: false,
                    error: e.message,
                });
            }
        })();
    }, []);

    const login = () => {
        window.location.href = "/api/auth/login";
    };
    const logout = async () => {
        await fetch("/api/auth/logout", { method: "POST" });
        window.location.href = "/";
    };

    // Route detection.
    const pathname =
        typeof window !== "undefined" ? window.location.pathname : "/";
    const isSplitRoute = pathname === "/split";
    const isServiceRoute = pathname === "/service";

    // /service uses the server-side Client Credentials flow and does NOT
    // require an end-user session. Render it before the auth gate.
    if (isServiceRoute) {
        return <ServiceView />;
    }

    if (authStatus.loading) {
        return (
            <div className="app">
                <div className="panel">
                    <p>Loading…</p>
                </div>
            </div>
        );
    }

    if (!authStatus.authenticated) {
        return (
            <div className="app">
                <div className="panel">
                    <h1>⚡ Lightning Out 2.0 × React POC</h1>
                    <p className="tagline">
                        React shell on Vercel + Salesforce LWC for authenticated
                        data.
                    </p>
                    <button className="btn" onClick={login}>
                        🔐 Login with Salesforce
                    </button>
                </div>
            </div>
        );
    }

    if (isSplitRoute) {
        return <SplitView onLogout={logout} />;
    }

    return (
        <div className="app">
            {mode === "list" && (
                <div className="panel">
                    <div
                        className="row"
                        style={{ justifyContent: "space-between" }}
                    >
                        <h1>⚡ Lightning Out 2.0 × React POC</h1>
                        <div className="row" style={{ gap: 8 }}>
                            <a className="btn secondary" href="/split">
                                Split view →
                            </a>
                            <a className="btn secondary" href="/service">
                                Service mode →
                            </a>
                        </div>
                    </div>
                    <p className="tagline">
                        Type a keyword below — results render inside the LWC.
                        Click a row to open its detail view.
                    </p>
                    <SearchInput value={keyword} onChange={setKeyword} />
                    <div className="meta">
                        Bridge: React ⇄ LWC via <code>keyword</code> attr,{" "}
                        <code>recordselect</code>, and <code>hostnavigate</code>{" "}
                        events.
                    </div>
                </div>
            )}

            <div className="panel">
                <div
                    className="row"
                    style={{ justifyContent: "space-between" }}
                >
                    <strong>
                        {mode === "list"
                            ? "Lightning Web Component (c-search-results)"
                            : "Lightning Web Component (c-record-detail)"}
                    </strong>
                    <button className="btn secondary" onClick={logout}>
                        🚪 Logout
                    </button>
                </div>
                <LightningOutHost
                    keyword={keyword}
                    mode={mode}
                    selectedRecord={selectedRecord}
                    onRecordSelect={handleRecordSelect}
                    onHostNavigate={handleHostNavigate}
                />
            </div>
        </div>
    );
}
